/**
 * Normalize fullwidth/compat separators so `_` detection matches copy-paste from PDFs, filenames, etc.
 */
function normalizeConfusableNameInput(value) {
  if (value == null) return '';
  let s = String(value).trim();
  try {
    s = s.normalize('NFKC');
  } catch {
    /* ignore */
  }
  return s
    .replace(/\uFF3F/g, '_')
    .replace(/\uFE4F/g, '_')
    .replace(/\uFE33/g, '_')
    .replace(/\u2017/g, '_');
}

function toTitleCase(value) {
  const raw = value == null ? '' : String(value).trim();
  if (!raw) return '';
  return raw
    .split(/\s+/)
    .map((word) =>
      word
        .split('-')
        .map((part) =>
          part ? `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}` : ''
        )
        .join('-')
    )
    .join(' ');
}

function sanitizeNameText(value) {
  if (!value || typeof value !== 'string') return '';
  let cleaned = String(value);
  if (cleaned.includes('@')) {
    const at = cleaned.indexOf('@');
    const beforeAt = cleaned.slice(0, at);
    const sepIndex = Math.max(beforeAt.lastIndexOf('-'), beforeAt.lastIndexOf('_'), beforeAt.lastIndexOf(' '));
    if (sepIndex > 0) {
      // Keep the filename/name prefix and drop trailing appended email.
      cleaned = cleaned.slice(0, sepIndex);
    }
  }
  cleaned = cleaned
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, ' ')
    .replace(/^(?:email|mail)\s+/i, ' ')
    .replace(/[|]+/g, ' ')
    .replace(/[/\\]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned;
}

const TRAILING_ROLE_PHRASES = [
  'sales professional',
  'medical representative',
  'sales manager',
  'business manager',
  'marketing executive',
  'software engineer',
  'data analyst',
  'customer relationship management',
];

const TRAILING_ROLE_TOKENS = new Set([
  'representative',
  'professional',
  'manager',
  'executive',
  'engineer',
  'developer',
  'analyst',
  'consultant',
  'specialist',
  'officer',
  'intern',
  'trainee',
  'dm',
  'senior',
  'senio',
]);

const TRAILING_LOCATION_TOKENS = new Set([
  'nashik',
  'pune',
  'mumbai',
  'delhi',
  'bangalore',
  'bengaluru',
  'hyderabad',
  'chennai',
  'kolkata',
  'ahmedabad',
  'jaipur',
  'indore',
  'nagpur',
  'noida',
  'gurgaon',
  'gurugram',
]);

const TRAILING_GENERIC_TOKENS = new Set([
  'add',
  'added',
  'new',
  'updated',
  'final',
  'latest',
  'resume',
  'cv',
  'profile',
  'email',
  'mail',
  'village',
  'vill',
  'dist',
  'district',
  'colony',
  'nagar',
  'linkedin',
  'gmail',
  'yahoo',
  'hotmail',
  'outlook',
  'medical',
  'nonmedical',
  'non-medical',
]);

/** Resume lines like "Add: - 123 Street" leak the word "Add" as a fake last name; strip even for 2-word names. */
const TRAILING_STRONG_ARTIFACT_TOKENS = new Set([
  'add',
  'added',
  'email',
  'mail',
  'village',
  'vill',
  'dist',
  'district',
  'linkedin',
  'medical',
  'nonmedical',
  'non-medical',
]);
const MONTH_ABBREV_TOKENS = new Set([
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'sept',
  'oct',
  'nov',
  'dec',
]);

function isInvalidStandaloneNameToken(token) {
  const t = String(token || '').trim().toLowerCase();
  if (!t) return true;
  return (
    TRAILING_GENERIC_TOKENS.has(t) ||
    TRAILING_ROLE_TOKENS.has(t) ||
    TRAILING_LOCATION_TOKENS.has(t) ||
    MONTH_ABBREV_TOKENS.has(t)
  );
}

function stripTrailingRoleText(value) {
  let s = sanitizeNameText(value);
  if (!s) return '';
  const lower = s.toLowerCase();
  for (const phrase of TRAILING_ROLE_PHRASES) {
    if (lower.endsWith(` ${phrase}`) || lower === phrase) {
      s = s.slice(0, s.length - phrase.length).trim();
      break;
    }
  }
  const parts = s.split(' ').filter(Boolean);
  while (parts.length > 1 && TRAILING_ROLE_TOKENS.has(parts[parts.length - 1].toLowerCase())) {
    parts.pop();
  }
  while (parts.length > 1 && TRAILING_LOCATION_TOKENS.has(parts[parts.length - 1].toLowerCase())) {
    parts.pop();
  }
  while (parts.length > 1 && TRAILING_STRONG_ARTIFACT_TOKENS.has(parts[parts.length - 1].toLowerCase())) {
    parts.pop();
  }
  while (parts.length > 2 && TRAILING_GENERIC_TOKENS.has(parts[parts.length - 1].toLowerCase())) {
    parts.pop();
  }
  return parts.join(' ').trim();
}

function normalizeRawNameCandidate(value) {
  let s = stripTrailingRoleText(value);
  if (!s) return '';
  // Remove leading generic labels and punctuation noise.
  s = s
    .replace(/^(?:name|candidate|resume|cv|profile)\s*[-:]?\s+/i, '')
    .replace(/^[^A-Za-z]+/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return s;
}

function deriveFromEmail(email) {
  if (!email || typeof email !== 'string') return '';
  const local = email.split('@')[0] || '';
  return local
    .replace(/[0-9]+/g, ' ')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function nameFromPossibleStructuredValue(value) {
  if (typeof value === 'string') return value.trim() || '';
  if (!value || typeof value !== 'object') return '';
  const fn =
    value.firstName ?? value.givenName ?? value.first_name ?? value.given_name;
  const mn = value.middleName ?? value.middle_name;
  const ln =
    value.lastName ?? value.familyName ?? value.surname ?? value.last_name ?? value.family_name;
  const pieces = [fn, mn, ln]
    .map((x) => (typeof x === 'string' ? x.trim() : ''))
    .filter(Boolean);
  if (pieces.length) return pieces.join(' ').trim();
  if (typeof value.fullName === 'string' && value.fullName.trim()) return value.fullName.trim();
  if (typeof value.name === 'string' && value.name.trim()) return value.name.trim();
  return '';
}

/** Higher score ⇒ more likely a resume filename / slug blob (try repair on these first). */
function filenameSlugHeuristicScore(s) {
  const t = String(s || '');
  let score = 0;
  if (/[0-9]{2,}/.test(t)) score += 5;
  else if (/[0-9]/.test(t)) score += 2;
  score += (t.match(/_/g) || []).length * 3;
  score += (t.match(/\./g) || []).length;
  if (/[a-z0-9](?:[._-][a-z0-9]+){2,}/i.test(t)) score += 4;
  return score;
}

function collectStructuredNameStrings(candidate) {
  const srt = candidate?.structuredResumeText;
  if (!srt || typeof srt !== 'object') return [];
  const seen = new Set();
  const out = [];
  const push = (raw) => {
    const s = normalizeConfusableNameInput(typeof raw === 'string' ? raw : String(raw || '')).trim();
    if (!s) return;
    const key = s.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  };

  const addPersonalBlock = (pi) => {
    if (!pi || typeof pi !== 'object') return;
    const nested = nameFromPossibleStructuredValue(pi.name);
    if (nested) push(nested);
    const flat = [pi.firstName, pi.middleName ?? pi.middle_name, pi.lastName]
      .map((x) => (typeof x === 'string' ? x.trim() : ''))
      .filter(Boolean)
      .join(' ')
      .trim();
    if (flat) push(flat);
  };

  // Prefer parser person blocks before top-level strings (often resume filenames).
  addPersonalBlock(srt.personalInformation);
  addPersonalBlock(srt.personalInfo);

  for (const value of [srt.basics?.name, srt.contactInfo?.name]) {
    const s = nameFromPossibleStructuredValue(value);
    if (s) push(s);
  }

  for (const value of [srt.name, srt.fullName, srt.candidateName]) {
    if (typeof value === 'string' && value.trim()) push(value.trim());
  }

  return out;
}

function pickBestStructuredDisplayName(candidates, candidate) {
  const list = (candidates || []).filter(Boolean);
  if (!list.length) return '';
  const meta = list.map((s) => ({
    s,
    plausible: isPlausiblePersonName(s, candidate),
    slug: filenameSlugHeuristicScore(s),
  }));
  const plausible = meta.filter((m) => m.plausible);
  if (plausible.length) {
    plausible.sort((a, b) => a.slug - b.slug || a.s.length - b.s.length);
    return plausible[0].s;
  }
  meta.sort((a, b) => a.slug - b.slug);
  return meta[0].s;
}

function extractStructuredName(candidate) {
  return pickBestStructuredDisplayName(collectStructuredNameStrings(candidate), candidate);
}

function looksLikeOrganizationName(value) {
  if (!value || typeof value !== 'string') return false;
  const normalized = value.toLowerCase();
  return /\b(pharma|technologies|technology|solutions?|systems?|labs?|healthcare|limited|ltd|llp|llc|inc|corp|company|private|laboratories)\b/.test(
    normalized
  );
}

function isPlausiblePersonName(value, candidate) {
  if (!value || typeof value !== 'string') return false;
  const cleaned = sanitizeNameText(value);
  if (!cleaned) return false;
  const words = cleaned.split(' ').filter(Boolean);
  if (words.length === 0 || words.length > 4) return false;
  if (words.some((w) => w.length > 30)) return false;
  if (looksLikeOrganizationName(cleaned)) return false;
  if (
    /\b(representative|sales professional|sales manager|business manager|officer|executive|engineer|developer|analyst|consultant|specialist|manager|intern|trainee)\b/i.test(
      cleaned
    )
  ) {
    return false;
  }

  const company = sanitizeNameText(candidate?.company || '');
  if (company && cleaned.toLowerCase() === company.toLowerCase()) return false;

  const emailLocal = String(candidate?.email || '')
    .split('@')[0]
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  const firstWord = String(words[0] || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  if (emailLocal && firstWord && emailLocal.length >= 4 && !emailLocal.includes(firstWord)) {
    return false;
  }

  return true;
}

function parseParts(raw) {
  const cleaned = normalizeRawNameCandidate(raw);
  if (!cleaned) return { firstName: '', lastName: '' };
  let words = cleaned.split(' ').filter(Boolean);
  while (words.length > 1 && isInvalidStandaloneNameToken(words[words.length - 1])) {
    words.pop();
  }
  if (words.length === 0) return { firstName: '', lastName: '' };
  if (words.length === 1) return { firstName: toTitleCase(words[0]), lastName: '' };
  // Enforce strict FirstName + LastName format.
  return {
    firstName: toTitleCase(words[0]),
    lastName: toTitleCase(words[words.length - 1]),
  };
}

/**
 * When `firstName` was polluted from a resume basename (underscores, role/location tokens)
 * but `lastName` is a proper field — derive a readable first name from the slug and keep lastName.
 * Mirrors how screening list PDFs prefer [firstName, lastName] when both exist.
 */
function repairSluggyFirstNamePair(firstName, lastName) {
  const lastRaw = normalizeConfusableNameInput(lastName);
  const firstRaw = normalizeConfusableNameInput(firstName);
  if (!firstRaw || !lastRaw) return null;
  const hasUnderscoreSlug = /_/.test(firstRaw);
  const hasMultiPartSlug = /^[a-z0-9]+(?:[._-][a-z0-9]+)+$/i.test(firstRaw);
  if (!hasUnderscoreSlug && !hasMultiPartSlug) return null;

  const expanded = stripTrailingRoleText(
    sanitizeNameText(firstRaw.replace(/_+/g, ' ').replace(/\.+/g, ' '))
  );
  if (!expanded) return null;
  let words = expanded.split(/\s+/).filter(Boolean);
  const lastLower = lastRaw.toLowerCase();
  // Drop trailing filename noise (dm, cities, etc.) before matching lastName — otherwise
  // e.g. [... khairnar, dm] never matches lastName and the surname stays inside "firstName".
  while (words.length > 1 && isInvalidStandaloneNameToken(words[words.length - 1])) {
    words.pop();
  }
  while (words.length > 1 && words[words.length - 1].toLowerCase() === lastLower) {
    words.pop();
  }
  while (words.length > 1 && isInvalidStandaloneNameToken(words[words.length - 1])) {
    words.pop();
  }
  if (words.length === 0) return null;

  const firstFromSlug = toTitleCase(words.join(' '));
  const lastClean = toTitleCase(normalizeRawNameCandidate(lastRaw));
  if (!firstFromSlug || !lastClean) return null;
  if (isInvalidStandaloneNameToken(lastClean)) return null;

  return {
    firstName: firstFromSlug,
    lastName: lastClean,
    fullName: `${firstFromSlug} ${lastClean}`.trim(),
  };
}

/**
 * Filename-style values with no spaces, only underscores, e.g.
 * "Nikunj_chhandrakantbhai_vyas_email" — repairMergedNameField used to require at least two words.
 */
function repairUnderscoreOnlyBlob(raw) {
  const normalized = normalizeConfusableNameInput(raw);
  if (!normalized || !/_/.test(normalized)) return null;
  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length !== 1) return null;

  let segs = normalized.split(/_+/).map((s) => s.trim()).filter(Boolean);
  if (segs.length < 2) return null;

  while (segs.length > 2 && segs[0].length === 1) {
    segs.shift();
  }
  while (segs.length > 1 && isInvalidStandaloneNameToken(segs[segs.length - 1])) {
    segs.pop();
  }
  if (segs.length < 2) return null;

  const lastRaw = segs[segs.length - 1];
  if (isInvalidStandaloneNameToken(lastRaw)) return null;
  const pseudoFirst = segs.slice(0, -1).join('_');
  const fromSlug = repairSluggyFirstNamePair(pseudoFirst, lastRaw);
  if (fromSlug) return fromSlug;

  // Plain two-part values like "Jigarsinh_rathod" are not sluggy enough for repairSluggyFirstNamePair.
  const firstSegs = pseudoFirst.split(/_+/).filter(Boolean);
  if (firstSegs.length === 1) {
    const a = toTitleCase(normalizeRawNameCandidate(firstSegs[0]));
    const b = toTitleCase(normalizeRawNameCandidate(lastRaw));
    if (a && b && !isInvalidStandaloneNameToken(a) && !isInvalidStandaloneNameToken(b)) {
      return { firstName: a, lastName: b, fullName: `${a} ${b}`.trim() };
    }
  }
  return null;
}

/**
 * Insert spaces for glued Latin names: "Nikivyas", "Shabeerhussain", or camelCase "NikiVyas".
 * Conservative: only when a split passes isPlausiblePersonName.
 */
function trySplitGluedLatinPersonName(raw, candidate) {
  if (!raw || typeof raw !== 'string') return null;
  const source = typeof candidate?.source === 'string' ? candidate.source.trim().toLowerCase() : '';
  const isShareLinkSelfApplied =
    source === 'screening_apply' || source === 'screening_start';
  const cleaned = normalizeRawNameCandidate(raw);
  if (!cleaned || /\s/.test(cleaned)) return null;

  // For share-link self-applied candidates, avoid aggressive guess-splitting of single lowercase
  // names (e.g. "Macmohan" -> "Macm Ohan"). If no explicit signal exists, keep as-entered.
  if (isShareLinkSelfApplied && !/[A-Z]/.test(raw) && !/[._-]/.test(raw)) {
    return null;
  }

  const camelSpaced = cleaned.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  if (/\s/.test(camelSpaced)) {
    const trial = toTitleCase(camelSpaced);
    if (isPlausiblePersonName(trial, candidate)) {
      const pr = parseParts(trial);
      if (pr.firstName && pr.lastName) {
        return {
          firstName: pr.firstName,
          lastName: pr.lastName,
          fullName: `${pr.firstName} ${pr.lastName}`.trim(),
        };
      }
    }
  }

  const oneWord = cleaned.replace(/\s+/g, '');
  if (!/^[A-Za-z]+$/.test(oneWord)) return null;
  const L = oneWord.length;
  if (L < 8 || L > 22) return null;

  const tryOrder =
    L >= 12
      ? [7, 6, 5, 8, 4, 3]
      : L >= 10
        ? [6, 5, 4, 7, 3]
        : [4, 5, 3];

  for (const lastLen of tryOrder) {
    if (lastLen < 3 || L - lastLen < 3) continue;
    const firstRaw = oneWord.slice(0, -lastLen);
    const lastRaw = oneWord.slice(-lastLen);
    if (L <= 11 && (firstRaw.length < 4 || lastRaw.length < 4)) continue;

    const trial = `${firstRaw} ${lastRaw}`;
    if (!isPlausiblePersonName(trial, candidate)) continue;
    const pr = parseParts(trial);
    if (pr.firstName && pr.lastName) {
      return {
        firstName: pr.firstName,
        lastName: pr.lastName,
        fullName: `${pr.firstName} ${pr.lastName}`.trim(),
      };
    }
  }
  return null;
}

function applyGluedNameExpansion(parts, candidate) {
  if (!parts || typeof parts !== 'object') return parts;
  const full =
    String(parts.fullName || '').trim() ||
    [parts.firstName, parts.lastName].filter(Boolean).join(' ').trim();
  if (!full) return parts;
  if (/\s/.test(full)) return parts;
  const split = trySplitGluedLatinPersonName(full, candidate);
  return split || parts;
}

/**
 * e.g. stored name "Aman" + email amankumar12apr@… → infer "Kumar" from the local part after the first name.
 */
function stripLikelyMonthDaySuffixFromEmailTail(tail) {
  return String(tail || '').replace(
    /(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\d*$/i,
    ''
  );
}

function tryExpandLastNameFromEmailLocal(parts, candidate) {
  if (!candidate?.email || typeof candidate.email !== 'string') return null;
  const first = String(parts.firstName || '').trim();
  let last = String(parts.lastName || '').trim();
  if (last || !first) return null;
  const fn = first.toLowerCase().replace(/[^a-z]/g, '');
  if (fn.length < 3) return null;
  const letters = candidate.email
    .split('@')[0]
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  if (!letters.startsWith(fn)) return null;
  let tail = letters.slice(fn.length).replace(/^\d+/, '');
  tail = stripLikelyMonthDaySuffixFromEmailTail(tail);
  if (tail.length < 3) return null;
  for (let end = Math.min(tail.length, 12); end >= 3; end--) {
    const candLast = tail.slice(0, end);
    if (isInvalidStandaloneNameToken(candLast)) continue;
    const trial = `${first} ${toTitleCase(candLast)}`;
    if (!isPlausiblePersonName(trial, candidate)) continue;
    const ln = toTitleCase(candLast);
    return {
      firstName: first,
      lastName: ln,
      fullName: `${first} ${ln}`.trim(),
    };
  }
  return null;
}

function applyEmailLastNameExpansion(parts, candidate) {
  const expanded = tryExpandLastNameFromEmailLocal(parts, candidate);
  return expanded || parts;
}

function toStrictFirstLastParts(parts) {
  const fullRaw =
    String(parts?.fullName || '').trim() ||
    [parts?.firstName, parts?.lastName].filter(Boolean).join(' ').trim();
  if (!fullRaw) return { firstName: '', lastName: '', fullName: '' };

  const parsed = parseParts(fullRaw);
  let first = parsed.firstName || toTitleCase(normalizeRawNameCandidate(parts?.firstName || ''));
  let last = parsed.lastName || toTitleCase(normalizeRawNameCandidate(parts?.lastName || ''));

  if (isInvalidStandaloneNameToken(last)) last = '';
  if (isInvalidStandaloneNameToken(first) && !last) first = '';

  // Collapse duplicated surnames caused by noisy source strings.
  if (first && last && first.toLowerCase() === last.toLowerCase()) last = '';

  const fullName = [first, last].filter(Boolean).join(' ').trim();
  return { firstName: first, lastName: last, fullName };
}

/**
 * Screening candidates are usually stored with a single `name` only (firstName/lastName are merged
 * on import and dropped). Filename-style values look like:
 * "Harish_khairnar_dm_nashik Khairnar" or spaced "Harish khairnar dm nashik Khairnar".
 */
function repairMergedNameField(name) {
  const raw = normalizeConfusableNameInput(name);
  if (!raw) return null;

  const underscoreOnly = repairUnderscoreOnlyBlob(raw);
  if (underscoreOnly) return underscoreOnly;

  const words = raw.split(/\s+/).filter(Boolean);
  if (words.length < 2) return null;
  const lastRaw = words[words.length - 1];
  if (isInvalidStandaloneNameToken(lastRaw)) return null;
  const firstBlob = words.slice(0, -1).join(' ');
  const firstBlobNorm = firstBlob.replace(/\s+/g, ' ').trim();
  const underscoreSlug = /_/.test(firstBlobNorm) || /_/.test(words[0]);
  const firstToken = words[0] || '';
  const multipartTokenSlug = /^[a-z0-9]+(?:[._-][a-z0-9]+){2,}$/i.test(firstToken);
  const shortened = stripTrailingRoleText(sanitizeNameText(firstBlobNorm));
  const junkStripped =
    shortened && shortened.length < firstBlobNorm.length && words.length >= 3;
  if (!underscoreSlug && !junkStripped && !multipartTokenSlug) return null;

  const pseudoFirst = underscoreSlug
    ? firstBlobNorm.includes('_')
      ? firstBlobNorm
      : words.slice(0, -1).join('_')
    : shortened.replace(/\s+/g, '_');
  if (!pseudoFirst) return null;

  return repairSluggyFirstNamePair(pseudoFirst, lastRaw);
}

function tryRepairMergedFromSources(candidate) {
  if (!candidate) return null;
  const structuredList = collectStructuredNameStrings(candidate);
  const structuredForRepair = [...structuredList].sort(
    (a, b) => filenameSlugHeuristicScore(b) - filenameSlugHeuristicScore(a)
  );
  const joinedFl = [candidate.firstName, candidate.lastName]
    .map((x) => normalizeConfusableNameInput(x))
    .filter(Boolean)
    .join(' ')
    .trim();
  const sources = [
    normalizeConfusableNameInput(candidate.name),
    ...structuredForRepair,
    joinedFl,
  ].filter((s) => typeof s === 'string' && s.trim());
  for (const src of sources) {
    const fixed = repairMergedNameField(src);
    if (fixed) return fixed;
  }
  return null;
}

function coerceFilenameLikeParts(parts) {
  if (!parts || typeof parts !== 'object') return parts;
  const full =
    String(parts.fullName || '').trim() ||
    [parts.firstName, parts.lastName].filter(Boolean).join(' ').trim();
  if (!full) return parts;
  const n = normalizeConfusableNameInput(full);
  const firstToken = (n.split(/\s+/)[0] || '').trim();
  const looksMergedSlug =
    /[a-z0-9]_[a-z0-9]/i.test(n) ||
    (firstToken && /^[a-z0-9]+(?:[._-][a-z0-9]+){2,}$/i.test(firstToken)) ||
    /[a-z0-9](?:[._-][a-z0-9]+){2,}/i.test(firstToken);
  if (!looksMergedSlug) return parts;
  const r = repairMergedNameField(n);
  return r && r.fullName ? r : parts;
}

function getCandidateNamePartsInner(candidate) {
  const repaired = repairSluggyFirstNamePair(candidate.firstName, candidate.lastName);
  if (repaired) return repaired;

  const repairedMerged = tryRepairMergedFromSources(candidate);
  if (repairedMerged) return repairedMerged;

  let first = candidate.firstName ? toTitleCase(normalizeRawNameCandidate(candidate.firstName)) : '';
  let last = candidate.lastName ? toTitleCase(normalizeRawNameCandidate(candidate.lastName)) : '';
  if (isInvalidStandaloneNameToken(last)) last = '';
  if (isInvalidStandaloneNameToken(first) && !last) first = '';
  if (first && last) {
    const repairedFl = repairMergedNameField([first, last].join(' ').trim());
    if (repairedFl) return repairedFl;
    const fullName = [first, last].filter(Boolean).join(' ').trim();
    return { firstName: first, lastName: last, fullName };
  }

  const fromFilename = candidate.resumeFilename ? String(candidate.resumeFilename).replace(/\.[^/.]+$/, '') : '';
  if (fromFilename) {
    const parts = parseParts(fromFilename);
    if (!first && parts.firstName) first = parts.firstName;
    if (!last && parts.lastName) last = parts.lastName;
    if (first && last) {
      const fullName = [first, last].join(' ').trim();
      return { firstName: first, lastName: last, fullName };
    }
  }

  const fromName = candidate.name ? String(candidate.name) : '';
  if (isPlausiblePersonName(fromName, candidate)) {
    const parts = parseParts(fromName);
    if (!first && parts.firstName) first = parts.firstName;
    if (!last && parts.lastName) last = parts.lastName;
    if (first && last) {
      const fullName = [first, last].join(' ').trim();
      return { firstName: first, lastName: last, fullName };
    }
  }

  const fromStructured = extractStructuredName(candidate);
  if (isPlausiblePersonName(fromStructured, candidate)) {
    const parts = parseParts(fromStructured);
    if (!first && parts.firstName) first = parts.firstName;
    if (!last && parts.lastName) last = parts.lastName;
    if (first && last) {
      const fullName = [first, last].join(' ').trim();
      return { firstName: first, lastName: last, fullName };
    }
  }

  const fromCandidateName = candidate.candidateName ? String(candidate.candidateName) : '';
  if (isPlausiblePersonName(fromCandidateName, candidate)) {
    const parts = parseParts(fromCandidateName);
    if (!first && parts.firstName) first = parts.firstName;
    if (!last && parts.lastName) last = parts.lastName;
    if (first && last) {
      const fullName = [first, last].join(' ').trim();
      return { firstName: first, lastName: last, fullName };
    }
  }

  const fromEmail = deriveFromEmail(candidate.email);
  if (fromEmail) {
    // If we already have first name, prefer structured extraction from email local-part
    // (e.g. amankumar12apr -> Kumar) over naive tail token (Apr).
    if (first && !last) {
      const expanded = tryExpandLastNameFromEmailLocal(
        { firstName: first, lastName: '', fullName: first },
        candidate
      );
      if (expanded?.lastName) last = expanded.lastName;
    }
    if (!last) {
      const parts = parseParts(fromEmail);
      if (!first && parts.firstName) first = parts.firstName;
      if (!last && parts.lastName) last = parts.lastName;
    }
  }

  const fullName = [first, last].filter(Boolean).join(' ').trim();
  return { firstName: first, lastName: last, fullName };
}

export function getCandidateNameParts(candidate) {
  if (!candidate) return { firstName: '', lastName: '', fullName: '' };
  const normalizedInput = {
    ...candidate,
    name: normalizeConfusableNameInput(candidate.name),
    firstName: normalizeConfusableNameInput(candidate.firstName),
    lastName: normalizeConfusableNameInput(candidate.lastName),
  };
  const coerced = coerceFilenameLikeParts(getCandidateNamePartsInner(normalizedInput));
  const expanded = applyGluedNameExpansion(
    applyEmailLastNameExpansion(coerced, normalizedInput),
    normalizedInput
  );
  return toStrictFirstLastParts(expanded);
}

/**
 * Display-only: title-case each word (and hyphenated segments). Use after resolution so list + modal match.
 */
export function formatDisplayPersonName(raw) {
  if (!raw || typeof raw !== 'string') return '';
  return raw
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (!word) return '';
      return word
        .split('-')
        .map((part) => (part.length ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''))
        .join('-');
    })
    .join(' ');
}

/**
 * Single entry point for UI labels: same resolution as {@link getCandidateNameParts}, plus email-stripping
 * and display title-case. Prefer this over raw `.fullName` in tables and modals.
 */
export function getCandidateDisplayName(candidate) {
  const source = typeof candidate?.source === 'string' ? candidate.source.trim().toLowerCase() : '';
  const isAnyScreeningSource = source.startsWith('screening_');
  const isFormInputScreeningSource = source === 'screening_apply' || source === 'screening_start';

  if (isAnyScreeningSource) {
    // Keep public + recruiter name rendering consistent with server canonical naming:
    // for form-input sources, always prefer the user-typed candidate name.
    if (isFormInputScreeningSource) {
      const rawTypedName = String(candidate?.name || '').replace(/\s+/g, ' ').trim();
      if (rawTypedName) {
        return formatDisplayPersonName(rawTypedName);
      }
    }
    // Prefer AI-derived name from structuredResumeText (populated after scoring or
    // via synthetic SRT for CSV/Excel uploads) over the regex-cleaned candidate.name.
    const structuredName = extractStructuredName(candidate);
    if (structuredName && structuredName.trim()) {
      return formatDisplayPersonName(structuredName.trim());
    }
    const raw = String(candidate?.name || '').replace(/\s+/g, ' ').trim();
    return raw;
  }

  const full = String(getCandidateNameParts(candidate).fullName || '').trim();
  if (!full) return '';
  const withoutEmail = full.replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, ' ').trim();
  const collapsed = withoutEmail.replace(/\s+/g, ' ').trim();
  return formatDisplayPersonName(collapsed);
}

